import { field } from '../../../field-types';
import { defineRepresentation, RepresentationCategory } from '../../../ontology.model';
import { RealNumberModel } from '../../primitives';

// Models
export const StandardFormParamsModel = {
  coefficient: field.number({
    label: 'a',
    description: 'The coefficient $a$ where $1 \\le a < 10$',
    example: 3.45,
    min: 1,
    max: 10,
    max_exclusive: true,
  }),
  exponent: field.integer({
    label: 'n',
    description: 'The integer exponent $n$',
    example: 4,
  }),
} as const;

export const StandardFormModel = {
  notation: field.latex({
    label: 'a \\times 10^n',
    description: 'The standard form notation ($a \\times 10^n$)',
    example: '3.45 \\times 10^4',
  }),
  coefficient: field.number({
    label: 'a',
    description: 'The coefficient $a$ where $1 \\le a < 10$',
    example: 3.45,
    min: 1,
    max: 10,
    max_exclusive: true,
  }),
  exponent: field.integer({
    label: 'n',
    description: 'The integer exponent $n$',
    example: 4,
  }),
} as const;

// Re-use the primitive RealNumberModel for Ordinary Number
export const OrdinaryNumberModel = RealNumberModel;

export const rep = {
  params: defineRepresentation({
    id: 'rep.num.sf.params',
    name: 'Standard Form Definition',
    tag_ids: ['tag.sf'],
    description: 'The definition of a number in standard form using coefficient $a$ and exponent $n$.',
    category: RepresentationCategory.Object,
    type: 'numerical',
    model: StandardFormParamsModel,
    template: 'a={{coefficient}}, n={{exponent}}',
    example_data: {
      coefficient: 3.45,
      exponent: 4,
    },
  }),

  sf_val: defineRepresentation({
    id: 'rep.num.sf.val',
    name: 'Standard Form',
    tag_ids: ['tag.sf'],
    description: 'A number written in the form $a \\times 10^n$ where $1 \\le a < 10$ and $n$ is an integer',
    category: RepresentationCategory.Form,
    type: 'numerical',
    model: StandardFormModel,
    template: '{{notation}}',
    example_data: {
      notation: '3.45 \\times 10^4',
      coefficient: 3.45,
      exponent: 4,
    },
  }),

  ord_val: defineRepresentation({
    id: 'rep.num.sf.ord',
    name: 'Ordinary Number',
    tag_ids: ['tag.sf.transform'],
    description: 'A number written in ordinary decimal notation',
    category: RepresentationCategory.Value,
    type: 'numerical',
    base_representation_id: 'rep.prim.real', // References the primitive Real Number
    model: OrdinaryNumberModel,
    template: '{{value}}',
    example_data: {
      value: 34500,
    },
  }),
};

export const standardFormRepresentations = Object.values(rep);

export const models = {
  'rep.num.sf.params': StandardFormParamsModel,
  'rep.num.sf.val': StandardFormModel,
  'rep.num.sf.ord': OrdinaryNumberModel,
} as const;
